import { useState } from "react";
import { ChevronUp, ChevronDown, SlidersHorizontal, Search, X, IndianRupee, CalendarRange, Clock, Plane, Tag, Sparkles, Globe2, MapPin } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { formatINR } from "@/data/tours";
import { DatePickerField } from "@/components/india/DatePickerField";
import { format, parseISO } from "date-fns";

const fmtDay = (s) => (s ? format(parseISO(s), "d MMM yy") : "Any");
import { NAV_LINKS } from "@/data/content";

// Gather the full list of cities for a scope from the mega-menu data
const collectCities = (scope) => {
  const label = scope === "world" ? "World" : "India";
  const mega = NAV_LINKS.find((l) => l.label === label)?.mega;
  const set = new Set();
  mega?.regions?.forEach((r) => r.groups?.forEach((g) => g.cities?.forEach((c) => set.add(c))));
  return [...set].sort((a, b) => a.localeCompare(b));
};

const Section = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-4 group"
        data-testid={`filter-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <span className="flex items-center gap-2.5 text-[13.5px] font-bold text-[var(--navy)]">
          {Icon && (
            <span className="w-7 h-7 rounded-lg bg-[var(--soft)] group-hover:bg-[#fcd34d]/20 flex items-center justify-center transition-colors">
              <Icon className="w-4 h-4 text-[var(--yellow-2)]" strokeWidth={1.9} />
            </span>
          )}
          {title}
        </span>
        <span className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${open ? "bg-[var(--navy)] text-white" : "bg-gray-100 text-gray-400 group-hover:bg-gray-200"}`}>
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </span>
      </button>
      <div className={`grid transition-all duration-300 ease-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden">
          <div className="pb-4">{children}</div>
        </div>
      </div>
    </div>
  );
};

const CheckRow = ({ label, count, checked, onToggle, testid }) => (
  <label
    className={`flex items-center gap-2.5 px-2 py-2 -mx-2 rounded-lg cursor-pointer transition-colors ${checked ? "bg-[#fcd34d]/12" : "hover:bg-gray-50"}`}
    data-testid={testid}
  >
    <Checkbox
      checked={checked}
      onCheckedChange={onToggle}
      className="rounded-[5px] border-gray-300 data-[state=checked]:bg-[var(--navy)] data-[state=checked]:border-[var(--navy)]"
    />
    <span className={`text-[13px] flex-1 transition-colors ${checked ? "text-[var(--navy)] font-medium" : "text-[#0b2545]/75"}`}>
      {label}
    </span>
    <span className={`text-[11px] px-1.5 py-0.5 rounded-md ${checked ? "bg-[var(--navy)] text-white" : "bg-gray-100 text-gray-500"}`}>
      {count}
    </span>
  </label>
);

const Pill = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-[12px] font-medium rounded-full border px-3 py-2 transition-all ${
      active
        ? "border-[var(--navy)] bg-[var(--navy)] text-white shadow-sm"
        : "border-gray-200 text-[#0b2545]/75 hover:border-[var(--navy)] hover:bg-[#0b2545]/[0.03]"
    }`}
  >
    {children}
  </button>
);

const Chip = ({ children, onRemove }) => (
  <span className="inline-flex items-center gap-1 bg-[var(--navy)] text-white text-[11px] font-medium pl-2.5 pr-1.5 py-1 rounded-full">
    {children}
    <button type="button" onClick={onRemove} aria-label="remove filter" className="w-4 h-4 rounded-full bg-white/20 hover:bg-[var(--red)] flex items-center justify-center transition-colors">
      <X className="w-2.5 h-2.5" />
    </button>
  </span>
);

export const FilterSidebar = ({ filters, setFilters, onReset, facets, scope = "india" }) => {
  const [cityDialogOpen, setCityDialogOpen] = useState(false);
  const [citySearch, setCitySearch] = useState("");
  const [countryQuery, setCountryQuery] = useState("");
  const [cityQuery, setCityQuery] = useState("");
  const has = (list, q) => list.filter((x) => x.label.toLowerCase().includes(q.trim().toLowerCase()));

  const toggleSet = (key, value) =>
    setFilters((f) => {
      const next = new Set(f[key]);
      next.has(value) ? next.delete(value) : next.add(value);
      return { ...f, [key]: next };
    });

  const allCities = collectCities(scope);
  const filteredDialogCities = allCities.filter((c) =>
    c.toLowerCase().includes(citySearch.trim().toLowerCase())
  );

  // Build the list of active filters (for the removable chip row + count badge)
  const priceActive = filters.price[0] !== 0 || filters.price[1] !== facets.priceMax;
  const chips = [];
  if (priceActive) chips.push({ key: "price", label: `${formatINR(filters.price[0])} – ${formatINR(filters.price[1])}`, remove: () => setFilters((f) => ({ ...f, price: [0, facets.priceMax] })) });
  if (filters.duration) chips.push({ key: "duration", label: filters.duration, remove: () => setFilters((f) => ({ ...f, duration: null })) });
  if (filters.packageType && filters.packageType !== "All") chips.push({ key: "pkg", label: filters.packageType, remove: () => setFilters((f) => ({ ...f, packageType: "All" })) });
  if (filters.dateFrom || filters.dateTo) chips.push({ key: "dates", label: `${fmtDay(filters.dateFrom)} → ${fmtDay(filters.dateTo)}`, remove: () => setFilters((f) => ({ ...f, dateFrom: "", dateTo: "" })) });
  ["departure", "joining", "speciality", "countries", "cities"].forEach((k) => {
    (filters[k] instanceof Set ? [...filters[k]] : []).forEach((v) =>
      chips.push({ key: `${k}-${v}`, label: v, remove: () => toggleSet(k, v) })
    );
  });
  const activeCount = chips.length;

  return (
    <aside className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-[0_20px_45px_-30px_rgba(11,37,69,0.5)]" data-testid="filter-sidebar">
      {/* Header — navy gradient */}
      <div className="bg-gradient-to-r from-[var(--navy)] via-[#12325c] to-[var(--navy)] px-5 py-4 flex items-center justify-between">
        <div className="inline-flex items-center gap-2.5 text-white font-bold">
          <span className="w-8 h-8 rounded-lg bg-[var(--yellow)] flex items-center justify-center">
            <SlidersHorizontal className="w-4 h-4 text-[var(--navy)]" />
          </span>
          <span className="leading-tight">
            Filter Your Tour
            <span className="block text-[11px] font-normal text-white/60">
              {activeCount > 0 ? `${activeCount} active` : "Refine your results"}
            </span>
          </span>
        </div>
        <button
          type="button"
          onClick={onReset}
          data-testid="filter-reset"
          className="text-[12px] font-semibold text-[var(--navy)] bg-[var(--yellow)] hover:bg-white px-3 py-1.5 rounded-full transition-colors"
        >
          Reset
        </button>
      </div>

      {/* Active filter chips */}
      {activeCount > 0 && (
        <div className="px-5 py-3 bg-[var(--soft)] border-b border-gray-100 flex flex-wrap gap-2" data-testid="active-filter-chips">
          {chips.map((c) => (
            <Chip key={c.key} onRemove={c.remove}>{c.label}</Chip>
          ))}
        </div>
      )}

      <div className="px-5">
        {/* Price */}
        <Section title="Price Range" icon={IndianRupee}>
          <div className="flex items-center gap-2 mb-4">
            <div className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-center">
              <div className="text-[9px] uppercase tracking-wider text-gray-400">Min</div>
              <div className="text-[13px] font-semibold text-[var(--navy)]">{formatINR(filters.price[0])}</div>
            </div>
            <span className="w-3 h-px bg-gray-300" />
            <div className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-center">
              <div className="text-[9px] uppercase tracking-wider text-gray-400">Max</div>
              <div className="text-[13px] font-semibold text-[var(--navy)]">{formatINR(filters.price[1])}</div>
            </div>
          </div>
          <Slider
            value={filters.price}
            min={0}
            max={facets.priceMax}
            step={5000}
            onValueChange={(v) => setFilters((f) => ({ ...f, price: v }))}
            className="my-3"
            data-testid="price-slider"
          />
          <div className="grid grid-cols-2 gap-2 mt-4">
            {facets.pricePresets.map((p) => {
              const active = filters.price[0] === p.min && filters.price[1] === p.max;
              return (
                <Pill key={p.label} active={active} onClick={() => setFilters((f) => ({ ...f, price: [p.min, p.max] }))} testid={`price-preset-${p.min}`}>
                  {p.label}
                </Pill>
              );
            })}
          </div>
        </Section>

        {/* Depart Between */}
        <Section title="Depart Between" icon={CalendarRange}>
          <div className="grid grid-cols-2 gap-2.5">
            <DatePickerField label="From" value={filters.dateFrom} max={filters.dateTo} onChange={(v) => setFilters((f) => ({ ...f, dateFrom: v }))} testId="depart-start" />
            <DatePickerField label="To" value={filters.dateTo} min={filters.dateFrom} onChange={(v) => setFilters((f) => ({ ...f, dateTo: v }))} testId="depart-end" />
          </div>
        </Section>

        {/* Duration */}
        <Section title="Tour Duration" icon={Clock}>
          <div className="grid grid-cols-2 gap-2">
            {facets.durationPresets.map((d) => {
              const active = filters.duration === d.label;
              return (
                <Pill key={d.label} active={active} onClick={() => setFilters((f) => ({ ...f, duration: active ? null : d.label }))} testid={`duration-${d.min}`}>
                  {d.label}
                </Pill>
              );
            })}
          </div>
        </Section>

        {/* Departure City */}
        <Section title="Departure City" icon={Plane}>
          <div className="space-y-0.5">
            {facets.departureCities.map((c) => (
              <CheckRow key={c.label} label={c.label} count={c.count} checked={filters.departure.has(c.label)} onToggle={() => toggleSet("departure", c.label)} testid={`depcity-${c.label}`} />
            ))}
          </div>
          {facets.joiningLeaving && (
            <div className="mt-3 pt-3 border-t border-dashed border-gray-200">
              <div className="text-[12.5px] font-bold text-[var(--navy)] mb-1">Joining &amp; Leaving</div>
              <p className="text-[11px] text-gray-400 mb-2 leading-snug">Book your own flights and join the tour at the first destination, leaving from the last.</p>
              <div className="space-y-0.5">
                {facets.joiningLeaving.map((c) => (
                  <CheckRow key={c.label} label={c.label} count={c.count} checked={filters.joining.has(c.label)} onToggle={() => toggleSet("joining", c.label)} testid={`jl-${c.label}`} />
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* Package Type */}
        <Section title="Package Type" icon={Tag}>
          <RadioGroup value={filters.packageType} onValueChange={(v) => setFilters((f) => ({ ...f, packageType: v }))} className="gap-0.5">
            {facets.packageTypes.map((p) => {
              const active = filters.packageType === p.label;
              return (
                <label key={p.label} className={`flex items-center gap-2.5 px-2 py-2 -mx-2 rounded-lg cursor-pointer transition-colors ${active ? "bg-[#fcd34d]/12" : "hover:bg-gray-50"}`} data-testid={`pkgtype-${p.label}`}>
                  <RadioGroupItem value={p.label} className="text-[var(--navy)] border-gray-300" />
                  <span className={`text-[13px] flex-1 ${active ? "text-[var(--navy)] font-medium" : "text-[#0b2545]/75"}`}>{p.label}</span>
                  <span className={`text-[11px] px-1.5 py-0.5 rounded-md ${active ? "bg-[var(--navy)] text-white" : "bg-gray-100 text-gray-500"}`}>{p.count}</span>
                </label>
              );
            })}
          </RadioGroup>
        </Section>

        {/* Speciality */}
        <Section title="Speciality Tour" icon={Sparkles}>
          <div className="space-y-0.5">
            {facets.speciality.map((s) => (
              <CheckRow key={s.label} label={s.label} count={s.count} checked={filters.speciality.has(s.label)} onToggle={() => toggleSet("speciality", s.label)} testid={`spec-${s.label}`} />
            ))}
          </div>
        </Section>

        {/* Countries */}
        <Section title="Countries" icon={Globe2}>
          <div className="relative mb-2.5">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={countryQuery} onChange={(e) => setCountryQuery(e.target.value)} data-testid="countries-search" placeholder="Search countries" className="pl-9 text-[13px] h-10 rounded-lg bg-[var(--soft)] border-transparent focus-visible:bg-white" />
          </div>
          <div className="space-y-0.5">
            {has(facets.countries, countryQuery).map((c) => (
              <CheckRow key={c.label} label={c.label} count={c.count} checked={filters.countries.has(c.label)} onToggle={() => toggleSet("countries", c.label)} testid={`country-${c.label}`} />
            ))}
          </div>
        </Section>

        {/* Cities */}
        <Section title="Cities" icon={MapPin} defaultOpen={false}>
          <div className="relative mb-2.5">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={cityQuery} onChange={(e) => setCityQuery(e.target.value)} data-testid="cities-search" placeholder="Search cities" className="pl-9 text-[13px] h-10 rounded-lg bg-[var(--soft)] border-transparent focus-visible:bg-white" />
          </div>
          <div className="space-y-0.5">
            {has(facets.cities, cityQuery).map((c) => (
              <CheckRow key={c.label} label={c.label} count={c.count} checked={filters.cities.has(c.label)} onToggle={() => toggleSet("cities", c.label)} testid={`city-${c.label}`} />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setCityDialogOpen(true)}
            data-testid="cities-view-more"
            className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-[var(--navy)] hover:text-[var(--yellow-2)] transition-colors"
          >
            View All Cities <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </Section>
      </div>

      {/* Cities popup — searchable multi-select */}
      <Dialog open={cityDialogOpen} onOpenChange={setCityDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="cities-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[var(--navy)] flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[var(--yellow-2)]" /> Select Cities
            </DialogTitle>
            <DialogDescription>
              Search and pick the cities you'd like to explore across {scope === "world" ? "the world" : "India"}.
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={citySearch}
              onChange={(e) => setCitySearch(e.target.value)}
              placeholder="Search cities"
              data-testid="cities-dialog-search"
              className="pl-9 h-11 rounded-lg bg-[var(--soft)] border-transparent focus-visible:bg-white"
            />
          </div>

          <div className="max-h-[46vh] overflow-y-auto thin-scroll grid grid-cols-2 gap-1 pr-1" data-testid="cities-dialog-list">
            {filteredDialogCities.map((c) => {
              const checked = filters.cities.has(c);
              return (
                <label
                  key={c}
                  data-testid={`dialog-city-${c}`}
                  className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${checked ? "bg-[#fcd34d]/15" : "hover:bg-gray-50"}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggleSet("cities", c)}
                    className="rounded-[5px] border-gray-300 data-[state=checked]:bg-[var(--navy)] data-[state=checked]:border-[var(--navy)]"
                  />
                  <span className={`text-[13px] ${checked ? "text-[var(--navy)] font-medium" : "text-[#0b2545]/75"}`}>{c}</span>
                </label>
              );
            })}
            {filteredDialogCities.length === 0 && (
              <div className="col-span-2 text-center text-gray-400 py-8 text-sm" data-testid="cities-dialog-empty">
                No cities found for “{citySearch}”.
              </div>
            )}
          </div>

          <DialogFooter className="flex-row items-center gap-3 sm:justify-between">
            <span className="text-[12px] text-gray-500 mr-auto" data-testid="cities-dialog-count">
              {filters.cities.size} selected
            </span>
            <Button
              type="button"
              variant="outline"
              onClick={() => setFilters((f) => ({ ...f, cities: new Set() }))}
              data-testid="cities-dialog-clear"
              className="border-gray-300 text-[var(--navy)]"
            >
              Clear
            </Button>
            <Button
              type="button"
              onClick={() => setCityDialogOpen(false)}
              data-testid="cities-dialog-apply"
              className="bg-[var(--navy)] hover:bg-[#12325c] text-white"
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </aside>
  );
};
